import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { SavedViewStorageHelper } from '../helpers/SavedViewStorageHelper';
import type { IReportSavedView } from '../types/report';

/** Built-in views plus the user's localStorage views, with mutators that persist only the latter. */
export const useSavedViews = (
  builtInViews: IReportSavedView[],
  corporationId: number
) => {
  const getSnapshot = useCallback(
    () => SavedViewStorageHelper.getSnapshot(corporationId),
    [corporationId]
  );
  const customViews = useSyncExternalStore(
    SavedViewStorageHelper.subscribe,
    getSnapshot,
    SavedViewStorageHelper.getServerSnapshot
  );

  const views = useMemo(
    () => [...builtInViews, ...customViews],
    [builtInViews, customViews]
  );

  const upsertView = useCallback(
    (view: IReportSavedView) => {
      if (view.isDefault) return;
      const exists = customViews.some((v) => v.id === view.id);
      SavedViewStorageHelper.persist(
        corporationId,
        exists
          ? customViews.map((v) => (v.id === view.id ? view : v))
          : [...customViews, view]
      );
    },
    [customViews, corporationId]
  );

  const removeView = useCallback(
    (id: string) => {
      if (builtInViews.some((v) => v.id === id)) return;
      SavedViewStorageHelper.persist(
        corporationId,
        customViews.filter((v) => v.id !== id)
      );
    },
    [customViews, builtInViews, corporationId]
  );

  return { views, upsertView, removeView };
};
